/**
 * An unbounded priority queue based on a priority heap. The elements of the priority queue are ordered according to
 * their natural ordering, or by a Comparator provided at queue construction time, depending on which constructor is
 * used. A priority queue does not permit null elements.
 *
 * The head of this queue is the least element with respect to the specified ordering. If multiple elements are tied for
 * least value, the head is one of those elements -- ties are broken arbitrarily. The queue retrieval operations remove,
 * peek, pop, and element access the element at the head of the queue.
 */
export class PriorityQueue<T> {
  private heap: T[] = [];

  public static instance<T>(): PriorityQueue<T> {
    return new PriorityQueue<T>((a, b) => a < b ? -1 : (a > b ? 1 : 0));
  }

  public static reverse<T>(): PriorityQueue<T> {
    return new PriorityQueue<T>((a, b) => a < b ? 1 : (a > b ? -1 : 0));
  }

  constructor(private comparator: (a: T, b: T) => number) {}

  size(): number {
    return this.heap.length;
  }

  isEmpty(): boolean {
    return this.heap.length <= 0;
  }

  /**
   * Retrieves, but does not remove, the head of this queue, or returns undefined if this queue is empty.
   */
  peek(): T | undefined {
    return this.heap.length > 0 ? this.heap[0] : undefined;
  }

  /**
   * Inserts the specified element(s) into this priority queue.
   *
   * @param element
   */
  push(...element: T[]): number {
    this.heap.push(...element.filter(v => v != null));
    this.heap.sort(this.comparator);
    return this.size();
  }

  /**
   * Retrieves and removes the head of this queue, or returns undefined if this queue is empty.
   */
  pop(): T | undefined {
    return this.heap.shift();
  }

  /**
   * Removes a single instance of the specified element from this queue, if it is present.
   *
   * @param element
   */
  remove(element: T): T[] | undefined {
    const idx = this.heap.indexOf(element);
    if (idx > -1) {
      return this.heap.splice(idx, 1);
    }
    return undefined;
  }

  /**
   * Returns an array containing all of the elements in this queue.
   */
  snapshot(): Readonly<T[]> {
    return [...this.heap];
  }
}
